<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { payingCommonConfig } from './configs/common'
import { api } from '@/api'
import { useToastStore } from '@/stores'
import { VIP_TYPE } from '@/constants'
import { useAxonPurchase } from '@/composables/axon/useAxonPurchase'
import { PaymentMethodEnum } from '@/composables/payment/constants'
import { trackGamePaymentPoint } from '@/composables/tracks/useGamePlayPoint'

const config = payingCommonConfig

const route = useRoute()
const { $toast } = useToastStore()
const { reportPurchase } = useAxonPurchase()

const orderId = route.query.orderId
const token = route.query.token
const pageType = route.query.type
const axon_price = route.query.axon_price as string
const axon_product_id = route.query.axon_product_id as string
const axon_item_name = route.query.axon_item_name as string
const payment_status = route.query.payment_status as string
const subscribeType = route.query.subscribeType as string
const discountOnceCoins = route.query.discountOnceCoins as string
const paymentChannel = route.query.paymentChannel as string
const extendProductId = route.query.extendProductId as string
const transactionId = route.query.session_id as string

let redirect_source = route.query.redirect_source as string
if (redirect_source && typeof redirect_source === 'string')
  redirect_source = window.decodeURIComponent(redirect_source)
else
  redirect_source = window.location.origin

let pollingTimer: any = null
let pollingCount = 0
const loadingVisible = ref(true)
let paySuccessTracked = false

function trackPaySuccess() {
  const payPanelType = Number(route.query.pay_panel_type)
  const payChannels: Record<string, number> = { paypal: PaymentMethodEnum.PAYPAL, stripe: PaymentMethodEnum.STRIPE }
  const payChannel = payChannels[paymentChannel]
  if (paySuccessTracked || ![1, 2].includes(payPanelType) || !payChannel)
    return

  paySuccessTracked = true
  trackGamePaymentPoint('pay_success', {
    pay_panel_type: payPanelType,
    pay_channel: payChannel,
  })
}

onMounted(async () => {
  // 从订阅过来
  if (pageType === 'subscribe' && payment_status === 'success') {
    const orderNum = route.query.orderNum as string
    const extendProductId = route.query.extendProductId as string

    payStatusPolling(true, orderNum, extendProductId)
    return
  }

  if (orderId && payment_status === 'success')
    payStatusPolling(false)

  else
    paymentRedirect(config.texts.paymentFailure)
})

function paymentRedirect(text: string) {
  $toast(text)
  setTimeout(() => {
    let url
    if (payment_status === 'success') {
      url = new URL(redirect_source)
      url.searchParams.set('payment_status', 'success')
      // 如果是折扣支付，返回折扣支付类型和一次性折扣金币
      if (Number(subscribeType) === VIP_TYPE.DISCOUNT) {
        url.searchParams.set('subscribeType', subscribeType)
        url.searchParams.set('discountOnceCoins', discountOnceCoins)
      }
    }
    else {
      url = redirect_source
    }
    window.location.replace(url)
  }, 1500)
}

// 支付验单
async function requestPayStatus() {
  try {
    const url = `/member/topup/verifyReceipt?orderNum=${orderId}&receipt=${paymentChannel === 'stripe' ? transactionId : token}`
    const urlWithTransactionId = paymentChannel === 'stripe' ? `${url}&transactionId=${transactionId}&extendProductId=${extendProductId}` : url
    const res: any = await api.post(urlWithTransactionId)
    if (res.code === 0 && res.data === true) {
      trackPaySuccess()
      reportPurchase({ item_id: axon_product_id, item_name: axon_item_name, price: axon_price }, orderId as string)
      paymentRedirect(config.texts.paymentSuccess)
      pollingTimer && clearInterval(pollingTimer)
    }
  }
  catch (error: any) {
    if (error?.code === 2003) {
      // 该订单在已经在处理中, 被加锁, 轮询再次请求会返回 2003, 不做任何操作
      // 不需要给用户提示
      console.warn('Payment order has been locked.')
    }
    else {
      pollingTimer && clearInterval(pollingTimer)
      paymentRedirect(config.texts.paymentFailure)
    }
  }
}

// 订阅验单
async function requestSubscribePayStatus(orderNum: string, extendProductId: string) {
  try {
    const url = `/member/subscribe/verifyReceipt?orderNum=${paymentChannel === 'stripe' ? orderId : orderNum}&receipt=${paymentChannel === 'stripe' ? transactionId : token}&extendProductId=${extendProductId}`
    const urlWithTransactionId = paymentChannel === 'stripe' ? `${url}&transactionId=${transactionId}` : url
    const res: any = await api.post(urlWithTransactionId)
    if (res.code === 0 && res.data === true) {
      trackPaySuccess()
      reportPurchase({ item_id: axon_product_id, item_name: axon_item_name, price: axon_price }, (paymentChannel === 'stripe' ? orderId : orderNum) as string)
      paymentRedirect(config.texts.paymentSuccess)
      pollingTimer && clearInterval(pollingTimer)
    }
  }
  catch (error: any) {
    if (error?.code === 2003) {
      console.warn('Payment order has been locked.')
    }
    else {
      pollingTimer && clearInterval(pollingTimer)
      paymentRedirect(config.texts.paymentFailure)
    }
  }
}
function payStatusPolling(isSubscribe = false, orderNum = '', extendProductId = '') {
  pollingTimer = setInterval(async () => {
    if (isSubscribe)
      requestSubscribePayStatus(orderNum, extendProductId)

    else
      requestPayStatus()

    pollingCount++
    if (pollingCount > 15) {
      pollingTimer && clearInterval(pollingTimer)
      paymentRedirect(config.texts.paymentFailure)
    }
  }, 2000)
}
</script>

<template>
  <div>
    <div v-if="loadingVisible" class="w-full h-100dvh bg-n-bg-global">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-8 font-bold text-center text-white">
        <span>{{ config.texts.rechargePleaseWait }}</span>
      </p>
    </div>
  </div>
</template>
